import React, { useState } from 'react';

// Kleurconstanten om de huisstijl te garanderen
const COLORS = {
  bgPink: '#FAEBEE',
  textPurple: '#854C73',
  textBrown: '#6b4912',
  borderPurple: 'rgba(133, 76, 115, 0.2)'
};

interface FAQItemProps {
  question: React.ReactNode;
  answer: string;
}

const FAQItem = ({ question, answer }: FAQItemProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div style={{ borderBottom: `1px solid ${COLORS.borderPurple}` }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: '1.25rem 0', // py-5
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          outline: 'none'
        }}
        // We gebruiken onMouseEnter/Leave voor hover effecten omdat CSS hover lastig is inline
        onMouseEnter={(e) => {
          const h3 = e.currentTarget.querySelector('h3');
          if(h3) h3.style.color = COLORS.textBrown;
        }}
        onMouseLeave={(e) => {
          const h3 = e.currentTarget.querySelector('h3');
          if(h3) h3.style.color = COLORS.textPurple;
        }}
        aria-expanded={isOpen}
      >
        {/* De Vraag */}
        <h3 
          className="text-lg md:text-xl"
          style={{ 
            color: COLORS.textPurple,
            fontFamily: 'serif',
            margin: 0,
            paddingRight: '1.5rem',
            transition: 'color 0.3s ease'
          }}
        >
          {question}
        </h3>
        
        {/* Het Icoontje */}
        <span 
          style={{ 
            color: COLORS.textPurple,
            display: 'flex',
            alignItems: 'center',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
            flexShrink: 0
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>
      
      {/* Het Antwoord */}
      <div 
        style={{ 
          display: 'grid',
          gridTemplateRows: isOpen ? '1fr' : '0fr',
          opacity: isOpen ? 1 : 0,
          marginBottom: isOpen ? '1.5rem' : '0',
          transition: 'all 0.3s ease-in-out'
        }}
      >
        <div style={{ overflow: 'hidden' }}>
          <p 
            className="text-sm sm:text-base"
            style={{ 
              color: '#4B5563', // text-gray-600
              lineHeight: '1.625',
              margin: 0,
              paddingRight: '1rem'
            }}
          >
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
};

// De Hoofdcontainer
const FAQAccordion = () => {
  const faqs = [
    {
      question: <>Wat zijn <span style={{ fontStyle: 'italic', fontWeight: 300, fontFamily: 'serif' }}>organisatieopstellingen</span>?</>,
      answer: "Organisatieopstellingen zijn een systemische methode waarbij organisatievraagstukken ruimtelijk worden weergegeven met representanten of symbolen. Het maakt onzichtbare patronen, verhoudingen en blokkades (de onderstroom) direct zichtbaar, zodat er beweging kan komen in wat vastzit."
    },
    {
      question: <>Wanneer worden <span style={{ fontStyle: 'italic', fontWeight: 300, fontFamily: 'serif' }}>organisatieopstellingen</span> ingezet?</>,
      answer: "Dit is effectief bij hoog verloop, terugkerende conflicten, gedoe of weerstand in teams, vastgelopen verandertrajecten, na fusies of overnames, bij leiderschapsvragen, of wanneer u merkt dat 'gewoon praten' of standaard coaching het probleem niet oplost. Vooral wanneer de dynamiek complex is en er onderhuidse spanning speelt."
    },
    {
      question: <>Wat is het verschil met <span style={{ fontStyle: 'italic', fontWeight: 300, fontFamily: 'serif' }}>teamcoaching</span>?</>,
      answer: "Teamcoaching richt zich vaak op gedrag en communicatie (de bovenstroom). Opstellingen werken in de diepere laag: gaat het over de juiste plek, ordening en balans in het systeem? We kijken naar de oorzaak, niet alleen naar het symptoom."
    },
    {
      question: <>Hoelang duurt een <span style={{ fontStyle: 'italic', fontWeight: 300, fontFamily: 'serif' }}>organisatieopstelling</span>?</>,
      answer: "Een organisatieopstelling duurt meestal 1,5 tot 2 uur per casus. Voor teamsessies plannen we vaak een dagdeel (4 uur) of een hele dag, afhankelijk van de vraag en de groepsgrootte."
    }
  ];

  return (
    <section style={{ width: '100%', marginTop: '4rem', marginBottom: '4rem' }}>
      {/* De Container */}
      <div 
        className="px-6 py-12 md:px-12"
        style={{ 
          backgroundColor: COLORS.bgPink,
          borderRadius: '0.75rem',
          maxWidth: '56rem', // max-w-4xl
          margin: '0 auto',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
        }}
      >
        
        {/* De Titel */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <h2 
            className="text-3xl md:text-4xl"
            style={{ 
              fontFamily: 'serif',
              color: COLORS.textBrown,
              marginBottom: '1rem',
              marginTop: 0
            }}
          >
            Veelgestelde <span className="italic font-light font-serif" style={{ color: COLORS.textPurple }}>vragen</span>
          </h2>
          <div 
            style={{ 
              width: '4rem',
              height: '2px',
              backgroundColor: COLORS.textPurple,
              margin: '0 auto',
              opacity: 0.5
            }}
          ></div>
        </div>

        {/* De Lijst */}
        <div 
          className="px-6 md:px-8"
          style={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            backdropFilter: 'blur(4px)',
            borderRadius: '0.5rem',
            border: `1px solid ${COLORS.borderPurple}`,
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
          }}
        >
          {faqs.map((faq, index) => (
            <FAQItem key={index} question={faq.question} answer={faq.answer} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQAccordion;

